import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from '@/components/ui/form';
import SectionHeader from '@/components/FullPageCarousel/SectionHeader';
import ErrorMessage from '@/components/ErrorMessage';
import { useSignupBasicDetailsForm } from '@/redux/auth/useAuthForm';
import { IUserSignupDataWithConfirmPassword } from '@/pages/Signup';
import { AuthProvider, IUser } from '@/redux/auth/auth.d';
import { debounce } from '@/lib/utils';
import { useLazyCheckUsernameAvailabilityQuery } from '@/redux/auth/auth.api.slice';
import { ProfilePhotoInput } from '@/components/ui/profile-photo-input';
import { usernameSchema } from '@/redux/auth/auth.schema';

function BasicDetails({
  user,
  formData,
  errors,
  setFormData,
}: {
  user: IUser | null;
  formData: IUserSignupDataWithConfirmPassword;
  errors: { [key: string]: string };
  setFormData: Dispatch<SetStateAction<IUserSignupDataWithConfirmPassword>>;
}) {
  const IS_USER_SINGUP_COMPLETE: boolean = Boolean(user?.isUserSignupComplete);
  const IS_SOCIAL_LOGIN: boolean = user?.authProvider !== AuthProvider.Email;

  const [isUsernameAvailable, setIsUsernameAvailable] = useState<string | null>(
    null
  );
  const [tempUsername, setTempUsername] = useState<string>('');

  const signupBaiscDetailsForm = useSignupBasicDetailsForm(
    !(Boolean(user) && IS_SOCIAL_LOGIN && !IS_USER_SINGUP_COMPLETE)
  );

  const [checkUsernameAvailabilityInDb] =
    useLazyCheckUsernameAvailabilityQuery();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const checkUsernameAvailability = useCallback(
    debounce(async (username: string) => {
      if (username.length < 4) {
        setIsUsernameAvailable('');
        return;
      }

      try {
        usernameSchema.parse(username);
      } catch (error: any) {
        setIsUsernameAvailable(error.errors[0].message);
        return;
      }

      setIsUsernameAvailable('checking');
      const { data: isAvailable } =
        await checkUsernameAvailabilityInDb(username);
      if (isAvailable) {
        setIsUsernameAvailable('available');
        setFormData((prev) => ({ ...prev, username }));
      } else {
        setIsUsernameAvailable('unavailable');
      }
    }, 300),
    []
  );

  const handleInputChange = (
    e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleUsernameChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setTempUsername(value);
    checkUsernameAvailability(value);
  };

  useEffect(() => {
    const formDataForReset = {
      ...formData,
      profilePicture:
        typeof formData.profilePicture === 'string'
          ? formData.profilePicture
          : undefined,
    };
    signupBaiscDetailsForm.reset(formDataForReset);
  }, [formData, signupBaiscDetailsForm]);

  const handlePhotoChange = (file: File | null) => {
    if (file) {
      setFormData((prev) => ({ ...prev, profilePicture: file }));
    } else {
      setFormData((prev) => ({ ...prev, profilePicture: '' }));
    }
  };

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Basic Details"
        description="Fill in the registration data. It will take a couple of minutes."
        align="left"
      />
      <div className="space-y-4">
        <Form {...signupBaiscDetailsForm}>
          <form autoComplete="off">
            <div className="flex flex-col py-2 gap-4">
              <div className="flex flex-col gap-2">
                <FormField
                  control={signupBaiscDetailsForm.control}
                  name="profilePicture"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Profile Photo</FormLabel>
                      <FormControl>
                        <ProfilePhotoInput
                          {...field}
                          value={formData.profilePicture}
                          onChange={handlePhotoChange}
                          className="h-40 w-40"
                        />
                      </FormControl>
                      <ErrorMessage error={errors.profilePicture} />
                    </FormItem>
                  )}
                />
              </div>
              <div className="flex flex-col gap-2">
                <FormField
                  control={signupBaiscDetailsForm.control}
                  name="username"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Username</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            placeholder="Enter your username"
                            {...field}
                            value={tempUsername}
                            onChange={handleUsernameChange}
                          />
                          {isUsernameAvailable && (
                            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                              {isUsernameAvailable === 'checking' && (
                                <Loader2 className="h-5 w-5 text-gray-400 animate-spin" />
                              )}
                              {isUsernameAvailable === 'available' && (
                                <CheckCircle2 className="h-5 w-5 text-green-500" />
                              )}
                              {isUsernameAvailable !== 'available' &&
                                isUsernameAvailable !== 'checking' && (
                                  <XCircle className="h-5 w-5 text-red-500" />
                                )}
                            </div>
                          )}
                        </div>
                      </FormControl>
                      <ErrorMessage error={errors.username} />
                      {isUsernameAvailable !== 'available' &&
                        isUsernameAvailable !== 'checking' && (
                          <p className="text-sm text-red-600">
                            {isUsernameAvailable}
                          </p>
                        )}
                    </FormItem>
                  )}
                />
              </div>
              <div className="flex items-center justify-between gap-4 w-full sm:flex-nowrap flex-wrap">
                <div className="flex flex-col gap-2 w-full">
                  <FormField
                    control={signupBaiscDetailsForm.control}
                    name="firstname"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Firstname</FormLabel>
                        <FormControl>
                          <Input
                            autoComplete="name"
                            placeholder="Enter your first name"
                            {...field}
                            onBlur={handleInputChange}
                          />
                        </FormControl>
                        <ErrorMessage error={errors.firstname} />
                      </FormItem>
                    )}
                  />
                </div>
                <div className="flex flex-col gap-2 w-full">
                  <FormField
                    control={signupBaiscDetailsForm.control}
                    name="lastname"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Lastname</FormLabel>
                        <FormControl>
                          <Input
                            autoComplete="family-name"
                            placeholder="Enter your last name"
                            {...field}
                            onBlur={handleInputChange}
                          />
                        </FormControl>
                        <ErrorMessage error={errors.lastname} />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <FormField
                  control={signupBaiscDetailsForm.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          autoComplete="email"
                          placeholder="Enter your email"
                          {...field}
                          disabled={
                            Boolean(user) &&
                            IS_SOCIAL_LOGIN &&
                            !IS_USER_SINGUP_COMPLETE
                          }
                          onBlur={handleInputChange}
                        />
                      </FormControl>
                      <ErrorMessage error={errors.email} />
                    </FormItem>
                  )}
                />
              </div>
              {!(
                Boolean(user) &&
                IS_SOCIAL_LOGIN &&
                !IS_USER_SINGUP_COMPLETE
              ) ? (
                <>
                  <div className="flex flex-col gap-2">
                    <FormField
                      control={signupBaiscDetailsForm.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Password</FormLabel>
                          <FormControl>
                            <Input
                              autoComplete="new-password"
                              type="password"
                              placeholder="Enter your password"
                              {...field}
                              onBlur={handleInputChange}
                            />
                          </FormControl>
                          <ErrorMessage error={errors.password} />
                        </FormItem>
                      )}
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <FormField
                      control={signupBaiscDetailsForm.control}
                      name="confirmPassword"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Confirm Password</FormLabel>
                          <FormControl>
                            <Input
                              autoComplete="new-password"
                              type="password"
                              placeholder="Enter your password"
                              {...field}
                              onBlur={handleInputChange}
                            />
                          </FormControl>
                          <ErrorMessage error={errors.confirmPassword} />
                        </FormItem>
                      )}
                    />
                  </div>
                </>
              ) : (
                // eslint-disable-next-line react/jsx-no-useless-fragment
                <></>
              )}
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
}

export default BasicDetails;
